/**
 * Image meta helpers — stores image focal point (position) + zoom as a URL fragment.
 *
 * Format:  https://path/to/image.jpg#pos=50_30&zoom=1.25
 *
 * Why fragments? Browsers strip fragments from HTTP requests, so CDN/storage
 * still serves the same file. We reuse the existing `image_url` text column
 * with zero DB migration, and it survives any backend untouched.
 */

const DEFAULT_POSITION = "50% 50%";
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

export function parseImageMeta(url) {
  if (!url || typeof url !== "string") {
    return { baseUrl: url || "", position: DEFAULT_POSITION, zoom: 1, x: 50, y: 50 };
  }
  const hashIdx = url.indexOf("#");
  if (hashIdx === -1) {
    return { baseUrl: url, position: DEFAULT_POSITION, zoom: 1, x: 50, y: 50 };
  }
  const baseUrl = url.slice(0, hashIdx);
  const hash = url.slice(hashIdx + 1);
  let x = 50, y = 50, zoom = 1;
  // Parse "pos=X_Y" and "zoom=Z" — tolerate other fragment content
  const params = new URLSearchParams(hash);
  const pos = params.get("pos");
  if (pos) {
    const [rawX, rawY] = pos.split("_");
    const nx = parseFloat(rawX);
    const ny = parseFloat(rawY);
    if (!Number.isNaN(nx)) x = Math.max(0, Math.min(100, nx));
    if (!Number.isNaN(ny)) y = Math.max(0, Math.min(100, ny));
  }
  const rawZoom = parseFloat(params.get("zoom"));
  if (!Number.isNaN(rawZoom)) {
    zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, rawZoom));
  }
  return { baseUrl, position: `${Math.round(x)}% ${Math.round(y)}%`, zoom, x, y };
}

export function buildImageMetaUrl(baseUrl, { x = 50, y = 50, zoom = 1 } = {}) {
  if (!baseUrl) return "";
  // Strip any existing fragment so we don't double-encode
  const clean = baseUrl.split("#")[0];
  const rx = Math.round(Math.max(0, Math.min(100, x)));
  const ry = Math.round(Math.max(0, Math.min(100, y)));
  const rz = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom || 1));
  const isDefault = rx === 50 && ry === 50 && Math.abs(rz - 1) < 0.01;
  if (isDefault) return clean;
  const parts = [];
  if (rx !== 50 || ry !== 50) parts.push(`pos=${rx}_${ry}`);
  if (Math.abs(rz - 1) >= 0.01) parts.push(`zoom=${rz.toFixed(2)}`);
  return `${clean}#${parts.join("&")}`;
}

export { MIN_ZOOM, MAX_ZOOM, DEFAULT_POSITION };
